import { PageHero, Reveal, SectionHead, PhotoFrame, EmptyState, Tag } from "@/components/Shared";
import { useCollection, IMG } from "@/lib/api";
import { BookOpen, Newspaper, ArrowUpRight } from "lucide-react";

export default function Publications() {
  const { items, loading } = useCollection("publications");
  const magazines = (items || []).filter((p) => p.kind === "magazine");
  const newsletters = (items || []).filter((p) => p.kind === "newsletter");

  return (
    <main>
      <PageHero eyebrow="Publications" title="Magazines &" italic="newsletters" image={IMG.celestialSky}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          A digital archive of everything we publish — flagship magazines and the bi-weekly newsletter that keeps the
          club connected.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="publications-magazines">
        <SectionHead eyebrow="Magazines" title="The flagship" italic="editions" />
        {loading && <p className="mt-10 text-sm text-silver-muted">Loading publications…</p>}
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {magazines.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1} className="h-full">
              <div className="card-line group flex h-full flex-col p-8 sm:flex-row sm:gap-8" data-testid={`magazine-card-${m.id}`}>
                <PhotoFrame src={m.cover_image} label="Magazine cover — add via Admin" className="aspect-[3/4] w-full bg-navy sm:w-48" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                <div className="mt-6 flex flex-1 flex-col sm:mt-0">
                  <Tag tone="ember">{m.period}</Tag>
                  <h3 className="mt-4 font-display text-3xl font-semibold metallic">{m.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-silver-muted">{m.description}</p>
                  <a
                    href={m.link || "#"}
                    onClick={(e) => !m.link && e.preventDefault()}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-silver mt-8 self-start"
                    data-testid={`magazine-read-btn-${m.id}`}
                  >
                    <span>{m.link ? "Read Magazine" : "Read Magazine — link coming"}</span>
                    <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
          {!loading && magazines.length === 0 && (
            <EmptyState title="No magazines yet" note="Annual magazines will be archived here." />
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28" data-testid="publications-newsletters">
        <SectionHead eyebrow="Bi-Weekly Newsletters" title="The" italic="archive" description="Every issue, beautifully preserved — a year-long archive by the end of 2026–27." />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {newsletters.map((n, i) => (
            <Reveal key={n.id} delay={(i % 4) * 0.07} className="h-full">
              <div className="card-line group flex h-full flex-col p-6" data-testid={`newsletter-card-${n.id}`}>
                <div className="flex items-center gap-2">
                  <Newspaper size={14} className="text-ember" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                    {n.month} {n.year}
                  </span>
                </div>
                <PhotoFrame src={n.cover_image} label="Newsletter cover" className="mt-5 aspect-[3/4] w-full bg-navy" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                <h3 className="mt-5 font-display text-2xl font-semibold text-silver">{n.title}</h3>
                <a
                  href={n.link || "#"}
                  onClick={(e) => !n.link && e.preventDefault()}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-silver-dim transition-all duration-300 hover:gap-3.5 hover:text-white"
                  data-testid={`newsletter-read-btn-${n.id}`}
                >
                  <BookOpen size={13} className="text-ember" /> {n.link ? "Read" : "Read — link coming"}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        {!loading && newsletters.length === 0 && (
          <div className="mt-10">
            <EmptyState title="The archive begins soon" note="Issue 01 will land here first." />
          </div>
        )}
      </section>
    </main>
  );
}
